import { useState, useRef, useEffect } from 'react';
import './CreatePublicActivityPanel.css';
import { communityEventsAPI } from './services/api';

function CreatePublicActivityPanel({ currentUser, onEventCreated }) {
  const [activityType, setActivityType] = useState('art');
  const [eventName, setEventName] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [eventPhoto, setEventPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [eventDate, setEventDate] = useState('');
  const [eventHour, setEventHour] = useState(0);
  const [eventMinute, setEventMinute] = useState(0);
  const [attendanceType, setAttendanceType] = useState('none');
  const [attendanceValue, setAttendanceValue] = useState('');
  const [creating, setCreating] = useState(false);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Activity type to EventType enum mapping
  const activityEventTypes = {
    art: 2,         // EventType.Art
    music: 3,       // EventType.Music
    sport: 4,       // EventType.Sport
    education: 6    // EventType.Education
  };

  // Helper function to update location
  const updateLocation = (lat, lng, formattedAddress) => {
    setAddress(formattedAddress);
    setLatitude(lat);
    setLongitude(lng);

    // Update input field
    if (addressInputRef.current) {
      addressInputRef.current.value = formattedAddress;
    }

    // Update marker on map
    if (markerRef.current && mapInstanceRef.current) {
      markerRef.current.setPosition({ lat, lng });
      mapInstanceRef.current.panTo({ lat, lng });
    }
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (!addressInputRef.current) {
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setTimeout(initAutocomplete, 100);
        return;
      }

      try {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          { types: ['geocode', 'establishment'] }
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place.geometry) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            updateLocation(lat, lng, place.formatted_address);
          }
        });

        autocompleteRef.current = autocomplete;
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };

    initAutocomplete();

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!mapRef.current) {
        return;
      }

      if (!window.google || !window.google.maps) {
        setTimeout(initMap, 100);
        return;
      }

      try {
        // Default to user's location or a central location
        const defaultLat = 42.6977;
        const defaultLng = 23.3219; // Sofia, Bulgaria

        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: defaultLat, lng: defaultLng },
          zoom: 13
        });

        const marker = new window.google.maps.Marker({
          map: map,
          position: { lat: defaultLat, lng: defaultLng },
          draggable: true
        });

        // When marker is dragged, update address
        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          const lat = position.lat();
          const lng = position.lng();

          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              updateLocation(lat, lng, results[0].formatted_address);
            }
          });
        });

        // When map is clicked, move marker
        map.addListener('click', (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();

          marker.setPosition({ lat, lng });

          // Reverse geocode to get address
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === 'OK' && results[0]) {
              updateLocation(lat, lng, results[0].formatted_address);
            }
          });
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    initMap();
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventPhoto(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventName.trim()) {
      alert('Please enter an event name');
      return;
    }

    if (!address || !latitude || !longitude) {
      alert('Please select a location on the map or enter an address');
      return;
    }

    if (isOrganizer && !eventDate) {
      alert('Please select a date and time');
      return;
    }

    // Validate that events requiring approval are at least 30 minutes in the future
    if (isOrganizer && eventDate) {
      const eventTime = `${String(eventHour).padStart(2, '0')}:${String(eventMinute).padStart(2, '0')}`;
      const eventDateTime = new Date(`${eventDate}T${eventTime}:00`);
      const now = new Date();
      const minFutureTime = new Date(now.getTime() + 30 * 60 * 1000); // 30 minutes from now

      if (eventDateTime < minFutureTime) {
        const timeDiff = Math.round((minFutureTime - eventDateTime) / (60 * 1000));
        if (eventDateTime < now) {
          alert('Public activities cannot be created for past dates. Please select a future date and time.');
        } else {
          alert(`Public activities requiring approval must be created at least 30 minutes before the start time. Your selected time is ${timeDiff} minute(s) too soon. Please choose a later time.`);
        }
        return;
      }
    }

    // Validate attendance value if type is selected
    if (attendanceType !== 'none') {
      if (!attendanceValue.trim() || isNaN(attendanceValue) || parseInt(attendanceValue) < 1) {
        alert('Please enter a valid attendance number (minimum 1)');
        return;
      }
    }

    try {
      setCreating(true);

      // Build attendance constraints
      let minAttendance = null;
      let maxAttendance = null;
      let fixedAttendance = null;

      if (attendanceType === 'min') {
        minAttendance = parseInt(attendanceValue);
      } else if (attendanceType === 'max') {
        maxAttendance = parseInt(attendanceValue);
      } else if (attendanceType === 'fixed') {
        fixedAttendance = parseInt(attendanceValue);
      }

      const eventData = {
        name: eventName,
        address: address,
        latitude: latitude,
        longitude: longitude,
        eventType: activityEventTypes[activityType],
        hashtags: [],
        photo: eventPhoto,
        minAttendance: minAttendance,
        maxAttendance: maxAttendance,
        fixedAttendance: fixedAttendance
      };

      if (isOrganizer && eventDate) {
        eventData.date = eventDate;
        eventData.time = `${String(eventHour).padStart(2, '0')}:${String(eventMinute).padStart(2, '0')}`;
      }

      await communityEventsAPI.create(eventData);
      alert('Public activity created successfully! It will appear in Community Work Discovery for community approval.');
      onEventCreated();
    } catch (error) {
      console.error('Error creating public activity:', error);
      const errorMessage = error.response?.data || error.message || 'Unknown error';
      alert(`Failed to create public activity: ${JSON.stringify(errorMessage)}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="create-public-activity-panel">
      <h3>Public Activity</h3>
      <p className="public-activity-description">
        Create a public activity event for your community. It requires 5 community approvals before becoming active.
      </p>

      <div className="event-form">
        <div className="form-group">
          <label>Activity Type</label>
          <select
            value={activityType}
            onChange={(e) => setActivityType(e.target.value)}
            className="activity-type-select"
          >
            <option value="art">Art</option>
            <option value="music">Music</option>
            <option value="sport">Sport</option>
            <option value="education">Education</option>
          </select>
        </div>

        <div className="form-group">
          <label>Event Name</label>
          <input
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="e.g., Open Air Concert, Art Exhibition, Football Match"
          />
        </div>

        <div className="form-group">
          <label>Event Photo (Optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="photo-input"
          />
          {photoPreview && (
            <div className="photo-preview">
              <img src={photoPreview} alt="Event preview" />
            </div>
          )}
          <p className="helper-text">Add a photo to help people understand what the event is about</p>
        </div>

        <div className="form-group datetime-section">
          <div className="radio-group">
            <label className={`radio-option${!isOrganizer ? ' selected' : ''}`}>
              <input
                type="radio"
                name="eventRole"
                checked={!isOrganizer}
                onChange={() => setIsOrganizer(false)}
              />
              <span className="radio-text">This is a gathering proposal</span>
            </label>
            <label className={`radio-option${isOrganizer ? ' selected' : ''}`}>
              <input
                type="radio"
                name="eventRole"
                checked={isOrganizer}
                onChange={() => setIsOrganizer(true)}
              />
              <span className="radio-text">I'm the organizer</span>
            </label>
          </div>
        </div>

        {isOrganizer && (
          <div className="form-group">
            <label>Date and Time</label>
            <div className="datetime-row">
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="date-input"
              />
              <div className="time-picker">
                <select
                  value={eventHour}
                  onChange={(e) => setEventHour(parseInt(e.target.value))}
                  className="time-select"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                  ))}
                </select>
                <span className="time-separator">:</span>
                <select
                  value={eventMinute}
                  onChange={(e) => setEventMinute(parseInt(e.target.value))}
                  className="time-select"
                >
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
                    <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="helper-text">Event must be at least 30 minutes in the future</p>
          </div>
        )}

        <div className="form-group">
          <label>Location</label>
          <input
            ref={addressInputRef}
            type="text"
            defaultValue={address}
            placeholder="Search for a location..."
            onBlur={(e) => setAddress(e.target.value)}
          />
          <p className="helper-text">Search for a place or click on the map below</p>

          {/* Google Map */}
          <div ref={mapRef} className="location-map"></div>
        </div>

        <div className="form-group attendance-section">
          <label>Attendance Constraints (Optional)</label>
          <div className="attendance-options">
            <label className="radio-label">
              <input
                type="radio"
                name="attendanceType"
                value="none"
                checked={attendanceType === 'none'}
                onChange={(e) => setAttendanceType(e.target.value)}
              />
              <span>No limit</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="attendanceType"
                value="min"
                checked={attendanceType === 'min'}
                onChange={(e) => setAttendanceType(e.target.value)}
              />
              <span>Minimum attendance</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="attendanceType"
                value="max"
                checked={attendanceType === 'max'}
                onChange={(e) => setAttendanceType(e.target.value)}
              />
              <span>Maximum attendance</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="attendanceType"
                value="fixed"
                checked={attendanceType === 'fixed'}
                onChange={(e) => setAttendanceType(e.target.value)}
              />
              <span>Fixed attendance</span>
            </label>
          </div>

          {attendanceType !== 'none' && (
            <div className="attendance-value">
              <input
                type="number"
                min="1"
                value={attendanceValue}
                onChange={(e) => setAttendanceValue(e.target.value)}
                placeholder="Enter number"
              />
              <p className="helper-text">
                {attendanceType === 'min' && 'Minimum number of people required'}
                {attendanceType === 'max' && 'Maximum number of people allowed'}
                {attendanceType === 'fixed' && 'Exact number of people expected'}
              </p>
            </div>
          )}
        </div>

        <button
          className="create-btn"
          onClick={handleCreateEvent}
          disabled={creating}
        >
          {creating ? 'Creating...' : 'Create Public Activity'}
        </button>
      </div>
    </div>
  );
}

export default CreatePublicActivityPanel;
